import type { JSX } from 'react';
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import type { InviteToOrganizationResult } from '@/features/membership/types';

/**
 * One-time display of the backend's temporary v1 raw-token delivery
 * (`inviteResultEnvelope`'s doc comment in membership.controller.ts: "no
 * email/SMTP delivery exists yet"). This component receives the result
 * only as a prop from the immediate invite/resend response - it is never
 * fetched again, never cached, never re-displayed from the list endpoint.
 * Closing this dialog discards the component and its local state; nothing
 * here is persisted. Mirrors `features/guardian/invitation-success-dialog.tsx`.
 */
export function InvitationSuccessDialog({
  result,
  onClose,
}: {
  readonly result: InviteToOrganizationResult | null;
  readonly onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  const acceptanceUrl =
    result !== null
      ? `${window.location.origin}/organization-invitations/accept?token=${result.rawToken}`
      : '';

  async function handleCopy(): Promise<void> {
    if (result === null) {
      return;
    }
    await navigator.clipboard.writeText(acceptanceUrl);
    setCopied(true);
  }

  function handleOpenChange(open: boolean): void {
    if (!open) {
      setCopied(false);
      onClose();
    }
  }

  return (
    <Dialog open={result !== null} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Undangan Terkirim</DialogTitle>
          <DialogDescription>
            Salin tautan ini dan kirimkan secara manual kepada orang yang diundang.
          </DialogDescription>
        </DialogHeader>

        {result !== null && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Email yang diundang</span>
              <span className="break-all text-sm text-muted-foreground">{result.invitedEmail}</span>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Kedaluwarsa</span>
              <span className="text-sm text-muted-foreground">
                {new Date(result.tokenExpiresAt).toLocaleString('id-ID')}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="acceptance-url">Tautan Penerimaan</Label>
              <div className="flex gap-2">
                <input
                  id="acceptance-url"
                  readOnly
                  value={acceptanceUrl}
                  className="min-w-0 flex-1 truncate rounded-md border border-input bg-muted px-3 py-2 text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Salin tautan"
                  onClick={() => {
                    void handleCopy();
                  }}
                >
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Selesai
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
